import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Avatar,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MessageCircle, ChevronLeft, Gamepad2, Clock, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import {
  Conversation,
  subscribeToConversations,
} from '../services/messageService';
import {
  TableInvite,
  subscribeToPendingInvites,
  acceptInvite,
  declineInvite,
} from '../services/inviteService';
import { tableService } from '../services/tableService';
import { gameApiService } from '../services/gameApiService';
import walletService from '../services/walletService';
import userService from '../services/userService';
import ChatModal from '../components/ChatModal';
import { POKER_COLORS } from '../theme/designSystem';

//  NEW: Stories imports

import StoriesBar from '../components/stories/StoriesBar';
import StoryViewer from '../components/stories/StoryViewer';
import FeedCard from '../components/stories/FeedCard';
import {
  StoryGroup,
  FeedPost,
  getFriendStories,
  getFeedPosts,
} from '../services/storyService';
import { getFriends } from '../services/socialService';

interface EnrichedConversation extends Conversation {
  otherUser: {
    odwinId: string;
    username: string;
    photoURL?: string;
  };
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const currentUserId = auth.currentUser?.uid;
  const currentUser = auth.currentUser;

  const [conversations, setConversations] = useState<EnrichedConversation[]>([]);
  const [invites, setInvites] = useState<TableInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  
  // Current user's profile (for username)
  const [currentUserProfile, setCurrentUserProfile] = useState<{username: string; photoURL?: string} | null>(null);
  
  // Chat modal state
  const [selectedUser, setSelectedUser] = useState<{
    userId: string;
    username: string;
    photoURL?: string;
  } | null>(null);
  const chatModal = useDisclosure();

  //  NEW: Stories state

  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [selectedStoryGroupIndex, setSelectedStoryGroupIndex] = useState<number | null>(null);
  
  //  NEW: Feed state

  const [feedPosts, setFeedPosts] = useState<FeedPost[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);

  // Load current user's profile for username
  useEffect(() => {
    if (!currentUserId) return;
    
    const loadCurrentUserProfile = async () => {
      try {
        const profile = await userService.getUserProfile(currentUserId);
        if (profile) {
          setCurrentUserProfile({
            username: profile.username || currentUser?.displayName || 'Anonymous',
            photoURL: profile.photoURL || currentUser?.photoURL || undefined,
          });
        }
      } catch (error) {
        console.error('Error loading current user profile:', error);
      }
    };
    
    loadCurrentUserProfile();
  }, [currentUserId, currentUser]);

  // Load stories
  useEffect(() => {
    if (!currentUserId) return;

    const loadStories = async () => {
      setStoriesLoading(true);
      try {
        const stories = await getFriendStories(currentUserId);
        setStoryGroups(stories);
      } catch (error) {
        console.error('Error loading stories:', error);
      } finally {
        setStoriesLoading(false);
      }
    };

    loadStories();
    // Refresh stories every 30 seconds
    const interval = setInterval(loadStories, 30000);
    return () => clearInterval(interval);
  }, [currentUserId]);

  // Load feed posts when Feed tab is selected
  useEffect(() => {
    if (!currentUserId || tabIndex !== 0) return;

    const loadFeed = async () => {
      setFeedLoading(true);
      try {
        const posts = await getFeedPosts(currentUserId, 20);
        setFeedPosts(posts);
      } catch (error) {
        console.error('Error loading feed:', error);
      } finally {
        setFeedLoading(false);
      }
    };

    loadFeed();
  }, [currentUserId, tabIndex]);

  // Load and enrich conversations
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribe = subscribeToConversations(currentUserId, async (convos) => {
      // Enrich conversations with user data
      const enriched: EnrichedConversation[] = [];
      
      for (const convo of convos) {
        // Find the other participant
        const otherUserId = convo.participants.find(p => p !== currentUserId);
        if (!otherUserId) continue;

        try {
          // Get other user's profile
          const userProfile = await userService.getUserProfile(otherUserId);
          
          enriched.push({
            ...convo,
            otherUser: {
              odwinId: otherUserId,
              username: userProfile?.username || 'Unknown',
              photoURL: userProfile?.photoURL,
            },
          });
        } catch (error) {
          console.error('Error enriching conversation:', error);
          enriched.push({
            ...convo,
            otherUser: {
              odwinId: otherUserId,
              username: 'Unknown',
            },
          });
        }
      }
      
      setConversations(enriched);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Subscribe to pending invites
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribe = subscribeToPendingInvites(currentUserId, (pendingInvites) => {
      setInvites(pendingInvites);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  const getExpiresIn = (expiresAt: any) => {
    if (!expiresAt) return '';
    const expires = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMs <= 0) return 'Expired';
    if (diffMins < 1) return `${diffSecs}s`;
    return `${diffMins}m ${diffSecs}s`;
  };

  const handleOpenChat = (convo: EnrichedConversation) => {
    setSelectedUser({
      userId: convo.otherUser.odwinId,
      username: convo.otherUser.username,
      photoURL: convo.otherUser.photoURL,
    });
    chatModal.onOpen();
  };

  const handleAcceptInvite = async (invite: TableInvite) => {
    if (!currentUserId) return;
    setProcessingInviteId(invite.id);

    try {
      // Group game invite: register (scheduled) or go to the group's live
      // table. Written by the server, so no table checks apply here.
      if (invite.kind === 'group_event') {
        await acceptInvite(invite.id);
        if (invite.eventId && !invite.tableId) await gameApiService.registerGroupEvent(invite.eventId, true).catch(() => {});
        navigate(invite.url || (invite.groupId ? `/groups/${invite.groupId}` : '/groups'));
        return;
      }
      if (invite.kind === 'group' && invite.groupId) {
        const r = await gameApiService.joinGroup({ groupId: invite.groupId, inviteId: invite.id });
        if (!r.success) throw new Error(r.error || 'Could not join the group');
        navigate(`/groups/${invite.groupId}`);
        return;
      }

      // Get table info to check currency and min buy-in
      const table = await tableService.getTable(invite.tableId);
      if (!table) {
        throw new Error('Table not found');
      }

      // Check balance for PSS Coins tables
      if (table.currency === 'pkcash') {
        const wallet = await walletService.getWallet(currentUserId);
        const pkCashBalance = wallet?.pkCash || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (pkCashBalance < minBuyIn) {
          toast({
            title: 'Insufficient PSS Coins',
            description: `You need at least $${minBuyIn} PSS Coins to join this table.`,
            status: 'error',
            duration: 5000,
          });
          await declineInvite(invite.id);
          setProcessingInviteId(null);
          return;
        }
      }

      // Check balance for PlayCoins tables
      if (table.currency === 'playcoins') {
        const wallet = await walletService.getWallet(currentUserId);
        const playCoinsBalance = wallet?.playCoins || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (playCoinsBalance < minBuyIn) {
          toast({
            title: 'Insufficient PlayCoins',
            description: `You need at least ${minBuyIn.toLocaleString()} PlayCoins to join.`,
            status: 'error',
            duration: 5000,
          });
          await declineInvite(invite.id);
          setProcessingInviteId(null);
          return;
        }
      }

      const tableId = await acceptInvite(invite.id);
      navigate(`/game/${tableId}`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to join table',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleDeclineInvite = async (invite: TableInvite) => {
    setProcessingInviteId(invite.id);
    try {
      await declineInvite(invite.id);
    } catch (error) {
      console.error('Error declining invite:', error);
    } finally {
      setProcessingInviteId(null);
    }
  };

  //  NEW: Handle story click

  const handleStoryClick = (group: StoryGroup, index: number) => {
    // Find the actual index in storyGroups
    const groupIndex = storyGroups.findIndex(g => g.odD === group.odD);
    if (groupIndex !== -1) {
      setSelectedStoryGroupIndex(groupIndex);
    }
  };

  //  NEW: Handle story deleted - refresh stories

  const handleStoryDeleted = async () => {
    if (!currentUserId) return;
    const stories = await getFriendStories(currentUserId);
    setStoryGroups(stories);
  };

  //  NEW: Navigate to user profile

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  return (
    <Box minH="100vh" bg="black" pb={{ base: '100px', md: '40px' }}>
      {/* Header */}
      <HStack 
        justify="space-between" 
        py={4} 
        px={4}
        borderBottom="1px solid"
        borderColor="gray.900"
        position="sticky"
        top={0}
        bg="black"
        zIndex={10}
      >
        <HStack spacing={3}>
          <Box
            as="button"
            onClick={() => navigate(-1)}
            p={1}
            _hover={{ opacity: 0.7 }}
          >
            <ChevronLeft size={24} color="white" />
          </Box>
          <HStack spacing={2}>
            <MessageCircle size={24} color="#f97316" />
            <Text color="white" fontSize="lg" fontWeight="semibold">
              Activity
            </Text>
          </HStack>
        </HStack>
      </HStack>

      {/*  NEW: Stories Bar */}

      <StoriesBar
        storyGroups={storyGroups}
        currentUserId={currentUserId || ''}
        loading={storiesLoading}
        onStoryClick={handleStoryClick}
        onAddStoryClick={() => {
          toast({
            title: 'Win a big pot!',
            description: 'Share your wins directly from the table after a big pot.',
            status: 'info',
            duration: 3000,
          });
        }}
      />

      {/* Tabs */}
      <Tabs 
        index={tabIndex} 
        onChange={setTabIndex}
        variant="unstyled"
        isFitted
      >
        <TabList 
          borderBottom="1px solid" 
          borderColor="gray.800"
          px={4}
        >
          {/*  NEW: Feed Tab */}

          <Tab
            py={3}
            color={tabIndex === 0 ? 'orange.400' : 'gray.500'}
            fontWeight="semibold"
            borderBottom="2px solid"
            borderColor={tabIndex === 0 ? 'orange.400' : 'transparent'}
            _focus={{ boxShadow: 'none' }}
          >
            <HStack spacing={2}>
              <Trophy size={16} />
              <Text>Feed</Text>
            </HStack>
          </Tab>
          <Tab
            py={3}
            color={tabIndex === 1 ? 'orange.400' : 'gray.500'}
            fontWeight="semibold"
            borderBottom="2px solid"
            borderColor={tabIndex === 1 ? 'orange.400' : 'transparent'}
            _focus={{ boxShadow: 'none' }}
          >
            <HStack spacing={2}>
              <Text>Chats</Text>
              {conversations.length > 0 && (
                <Badge 
                  bg="gray.700" 
                  color="gray.300" 
                  borderRadius="full" 
                  px={2}
                  fontSize="xs"
                >
                  {conversations.length}
                </Badge>
              )}
            </HStack>
          </Tab>
          <Tab
            py={3}
            color={tabIndex === 2 ? 'orange.400' : 'gray.500'}
            fontWeight="semibold"
            borderBottom="2px solid"
            borderColor={tabIndex === 2 ? 'orange.400' : 'transparent'}
            _focus={{ boxShadow: 'none' }}
          >
            <HStack spacing={2}>
              <Text>Invites</Text>
              {invites.length > 0 && (
                <Badge 
                  bg="red.500" 
                  color="white" 
                  borderRadius="full" 
                  px={2}
                  fontSize="xs"
                >
                  {invites.length}
                </Badge>
              )}
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/*  NEW: Feed Panel */}

          <TabPanel p={4}>
            {feedLoading ? (
              <VStack py={20}>
                <Spinner color="orange.400" size="xl" />
                <Text color="gray.500" fontSize="sm">Loading feed...</Text>
              </VStack>
            ) : feedPosts.length === 0 ? (
              <VStack py={20} px={4}>
                <Box
                  w="80px"
                  h="80px"
                  bg="gray.900"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <Trophy size={40} color="#666" />
                </Box>
                <Text color="gray.400" fontSize="lg" fontWeight="semibold">
                  No wins shared yet
                </Text>
                <Text color="gray.600" fontSize="sm" textAlign="center" maxW="280px">
                  Win big pots and share them with friends! Your wins will appear here.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={{ base: 0, md: 4 }} align="stretch">
                {feedPosts.map((post) => (
                  <FeedCard
                    currentUsername={currentUserProfile?.username || currentUser?.displayName || 'Anonymous'}
                    currentAvatarUrl={currentUserProfile?.photoURL || currentUser?.photoURL || undefined}
                    onDeleted={() => setFeedPosts(prev => prev.filter(p => p.id !== post.id))}
                    key={post.id}
                    story={post}
                    currentUserId={currentUserId || ''}
                    onUserClick={handleUserClick}
                    onImageClick={() => {
                      // Find this post's group and show in viewer
                      const groupIndex = storyGroups.findIndex(g => g.odD === post.odD);
                      if (groupIndex !== -1) {
                        setSelectedStoryGroupIndex(groupIndex);
                      }
                    }}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>

          {/* Chats Panel */}
          <TabPanel p={0}>
            {loading ? (
              <VStack py={20}>
                <Spinner color="orange.400" size="xl" />
                <Text color="gray.500" fontSize="sm">Loading messages...</Text>
              </VStack>
            ) : conversations.length === 0 ? (
              <VStack py={20} px={4}>
                <Box
                  w="80px"
                  h="80px"
                  bg="gray.900"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <MessageCircle size={40} color="#666" />
                </Box>
                <Text color="gray.400" fontSize="lg" fontWeight="semibold">
                  No messages yet
                </Text>
                <Text color="gray.600" fontSize="sm" textAlign="center" maxW="280px">
                  Start a conversation with a friend by visiting their profile and tapping "Send Message"
                </Text>
              </VStack>
            ) : (
              <VStack spacing={0} align="stretch">
                {conversations.map((convo) => {
                  // Check if conversation is unread based on lastReadAt
                  let isUnread = false;
                  if (convo.lastMessage && convo.lastMessageBy !== currentUserId) {
                    const lastReadAt = (convo as any).lastReadAt?.[currentUserId || ''];
                    if (!lastReadAt) {
                      isUnread = true; // Never read
                    } else {
                      const lastMessageTime = convo.lastMessageAt?.toMillis ? convo.lastMessageAt.toMillis() : 0;
                      const lastReadTime = lastReadAt?.toMillis ? lastReadAt.toMillis() : 0;
                      isUnread = lastMessageTime > lastReadTime;
                    }
                  }
                  
                  return (
                    <Box
                      key={convo.id}
                      px={4}
                      py={3}
                      cursor="pointer"
                      _hover={{ bg: 'gray.900' }}
                      transition="background 0.2s"
                      onClick={() => handleOpenChat(convo)}
                      borderBottom="1px solid"
                      borderColor="gray.900"
                    >
                      <HStack spacing={3}>
                        <Box position="relative">
                          <Avatar
                            size="md"
                            name={convo.otherUser.username}
                            src={convo.otherUser.photoURL}
                            bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                          />
                          {isUnread && (
                            <Box
                              position="absolute"
                              top="-2px"
                              right="-2px"
                              w="14px"
                              h="14px"
                              bg="orange.500"
                              borderRadius="full"
                              border="2px solid black"
                            />
                          )}
                        </Box>
                        <VStack align="start" spacing={0} flex={1} overflow="hidden">
                          <HStack justify="space-between" w="100%">
                            <Text 
                              color="white" 
                              fontWeight={isUnread ? 'bold' : 'semibold'}
                              fontSize="sm"
                            >
                              {convo.otherUser.username}
                            </Text>
                            <Text color="gray.500" fontSize="xs">
                              {formatTimeAgo(convo.lastMessageAt)}
                            </Text>
                          </HStack>
                          <Text 
                            color={isUnread ? 'white' : 'gray.500'} 
                            fontSize="sm" 
                            isTruncated
                            fontWeight={isUnread ? 'medium' : 'normal'}
                          >
                            {convo.lastMessageBy === currentUserId ? 'You: ' : ''}
                            {convo.lastMessage || 'No messages yet'}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </TabPanel>

          {/* Invites Panel */}
          <TabPanel p={0}>
            {invites.length === 0 ? (
              <VStack py={20} px={4}>
                <Box
                  w="80px"
                  h="80px"
                  bg="gray.900"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <Gamepad2 size={40} color="#666" />
                </Box>
                <Text color="gray.400" fontSize="lg" fontWeight="semibold">
                  No pending invites
                </Text>
                <Text color="gray.600" fontSize="sm" textAlign="center" maxW="280px">
                  When friends invite you to join their table, it will appear here
                </Text>
              </VStack>
            ) : (
              <VStack spacing={0} align="stretch">
                {invites.map((invite) => (
                  <Box
                    key={invite.id}
                    px={4}
                    py={4}
                    borderBottom="1px solid"
                    borderColor="gray.900"
                  >
                    <HStack spacing={3}>
                      <Box
                        w="50px"
                        h="50px"
                        bg="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Gamepad2 size={24} color="white" />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text color="white" fontWeight="semibold" fontSize="sm">
                          {invite.fromUsername} invited you!
                        </Text>
                        <Text color="gray.400" fontSize="xs">
                          {(invite as any).kind === 'group'
                            ? `Group: ${invite.tableName}`
                            : (invite as any).kind === 'group_event'
                              ? `${invite.tableName}${(invite as any).groupName ? ` (${(invite as any).groupName})` : ''}`
                              : `${invite.tableName} • $${invite.smallBlind}/$${invite.bigBlind}`}
                        </Text>
                        <HStack spacing={1}>
                          <Clock size={12} color="#9ca3af" />
                          <Text color="gray.500" fontSize="xs">
                            Expires in {getExpiresIn(invite.expiresAt)}
                          </Text>
                        </HStack>
                      </VStack>
                      <VStack spacing={2}>
                        <Button
                          size="sm"
                          bg="green.500"
                          color="white"
                          _hover={{ bg: 'green.600' }}
                          isLoading={processingInviteId === invite.id}
                          onClick={() => handleAcceptInvite(invite)}
                          px={4}
                        >
                          {(invite as any).kind === 'group_event' && !invite.tableId ? 'Register' : 'Join'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          color="gray.400"
                          _hover={{ bg: 'gray.800' }}
                          isLoading={processingInviteId === invite.id}
                          onClick={() => handleDeclineInvite(invite)}
                          px={4}
                        >
                          Decline
                        </Button>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Chat Modal */}
      {selectedUser && (
        <ChatModal
          isOpen={chatModal.isOpen}
          onClose={() => {
            chatModal.onClose();
            setSelectedUser(null);
          }}
          userId={selectedUser.userId}
          username={selectedUser.username}
          photoURL={selectedUser.photoURL}
          onNavigateToFeed={(storyId) => {
            chatModal.onClose();
            setSelectedUser(null);
            setTabIndex(0); // Switch to Feed tab (index 0)
            // TODO: Could scroll to specific story using storyId
          }}
        />
      )}

      {/*  NEW: Story Viewer */}

      {selectedStoryGroupIndex !== null && storyGroups.length > 0 && (
        <StoryViewer
          currentUsername={currentUserProfile?.username || currentUser?.displayName || 'Anonymous'}
          currentAvatarUrl={currentUserProfile?.photoURL || currentUser?.photoURL || undefined}
          storyGroups={storyGroups}
          initialGroupIndex={selectedStoryGroupIndex}
          currentUserId={currentUserId || ''}
          onClose={() => setSelectedStoryGroupIndex(null)}
          onStoryDeleted={handleStoryDeleted}
        />
      )}
    </Box>
  );
};

export default MessagesPage;